import {resolveVars} from '../../contentGenerator/formulaUtils';
import {
    TITLE_LIMITS,
    DESCRIPTION_LIMITS,
    BULLET_LIMITS,
    SCORE_WEIGHTS,
    PENALTY,
    GRADES,
    POWER_WORDS,
    CTA_WORDS,
    STOP_WORDS,
    FILLER_WORDS,
    PASSIVE_MARKERS,
    KEY_SPEC_VARS,
    READABILITY,
    CHECK_STATUS,
} from './seoConstants';

const {PASS, WARN, FAIL, INFO, BONUS} = CHECK_STATUS;

// ── Text utilities ──────────────────────────────────────────────────

const words = (text) => (text || '').trim().split(/\s+/).filter(Boolean);
const sentences = (text) => (text || '').split(/[.!?]+/).map((s) => s.trim()).filter(Boolean);
const wordCount = (text) => words(text).length;
const charCount = (text) => (text || '').trim().length;

const countOccurrences = (text, term) => {
    if (!text || !term) return 0;
    const lower = text.toLowerCase();
    const lowerTerm = term.toLowerCase();
    let count = 0;
    let pos = 0;
    while ((pos = lower.indexOf(lowerTerm, pos)) !== -1) {
        count++;
        pos += lowerTerm.length;
    }
    return count;
};

const jaccardSimilarity = (textA, textB) => {
    const setA = new Set(words(textA.toLowerCase()));
    const setB = new Set(words(textB.toLowerCase()));
    if (!setA.size || !setB.size) return 0;
    let intersection = 0;
    setA.forEach((w) => { if (setB.has(w)) intersection++; });
    return intersection / (setA.size + setB.size - intersection);
};

const hasAllCaps = (text) => {
    const allCapsWords = words(text).filter((w) => w.length > 1 && w === w.toUpperCase() && /[A-Z]/.test(w));
    return allCapsWords.length;
};

const containsAny = (text, wordSet) => {
    const lower = (text || '').toLowerCase();
    for (const word of wordSet) {
        if (lower.includes(word)) return true;
    }
    return false;
};

const findMatches = (text, wordSet) => {
    const lower = (text || '').toLowerCase();
    const found = [];
    for (const word of wordSet) {
        if (lower.includes(word)) found.push(word);
    }
    return found;
};

// ── Syllable counter (for Flesch score) ─────────────────────────────

const countSyllables = (word) => {
    const w = word.toLowerCase().replace(/[^a-z]/g, '');
    if (w.length <= 2) return 1;
    let count = 0;
    const vowels = 'aeiouy';
    let prevVowel = false;
    for (let i = 0; i < w.length; i++) {
        const isVowel = vowels.includes(w[i]);
        if (isVowel && !prevVowel) count++;
        prevVowel = isVowel;
    }
    if (w.endsWith('e') && count > 1) count--;
    return Math.max(count, 1);
};

const totalSyllables = (text) =>
    words(text).reduce((sum, w) => sum + countSyllables(w), 0);

// ── Flesch Reading Ease ─────────────────────────────────────────────

const fleschReadingEase = (text) => {
    const wc = wordCount(text);
    const sc = sentences(text).length || 1;
    const sy = totalSyllables(text);
    if (!wc) return 0;
    return Math.round(206.835 - 1.015 * (wc / sc) - 84.6 * (sy / wc));
};

const fleschToGradeLabel = (score) => {
    if (score >= 80) return 'Easy (5th grade)';
    if (score >= 70) return 'Fairly easy (6th grade)';
    if (score >= 60) return 'Standard (7th-8th grade)';
    if (score >= 50) return 'Fairly difficult (10th grade)';
    if (score >= 30) return 'Difficult (college)';
    return 'Very difficult (graduate)';
};

// ── Check builder ───────────────────────────────────────────────────

const check = (id, label, status, message, value = '') => ({id, label, status, message, value});

// ── Title checks ────────────────────────────────────────────────────

function checkTitleLength(text) {
    const len = charCount(text);
    if (len < TITLE_LIMITS.minChars)
        return check('title-length', 'Title length', FAIL, `Too short (${len} chars). Aim for ${TITLE_LIMITS.optimalMinChars}-${TITLE_LIMITS.optimalMaxChars}.`, `${len} chars`);
    if (len > TITLE_LIMITS.maxChars)
        return check('title-length', 'Title length', FAIL, `Too long (${len} chars). Keep under ${TITLE_LIMITS.maxChars}.`, `${len} chars`);
    if (len > TITLE_LIMITS.optimalMaxChars)
        return check('title-length', 'Title length', WARN, `Slightly long (${len} chars). Optimal: ${TITLE_LIMITS.optimalMinChars}-${TITLE_LIMITS.optimalMaxChars}.`, `${len} chars`);
    if (len < TITLE_LIMITS.optimalMinChars)
        return check('title-length', 'Title length', WARN, `Slightly short (${len} chars). Optimal: ${TITLE_LIMITS.optimalMinChars}-${TITLE_LIMITS.optimalMaxChars}.`, `${len} chars`);
    return check('title-length', 'Title length', PASS, `${len} chars (optimal range).`, `${len} chars`);
}

function checkTitleWordCount(text) {
    const count = wordCount(text);
    if (count < TITLE_LIMITS.minWords)
        return check('title-words', 'Word count', WARN, `Only ${count} words. Aim for ${TITLE_LIMITS.minWords}-${TITLE_LIMITS.maxWords}.`, `${count} words`);
    if (count > TITLE_LIMITS.maxWords)
        return check('title-words', 'Word count', WARN, `${count} words is verbose. Aim for ${TITLE_LIMITS.minWords}-${TITLE_LIMITS.maxWords}.`, `${count} words`);
    return check('title-words', 'Word count', PASS, `${count} words.`, `${count} words`);
}

function checkTitleKeywords(text, searchTerms) {
    if (!searchTerms.length)
        return check('title-keywords', 'Keywords in title', INFO, 'No search terms defined.', 'N/A');
    const present = searchTerms.filter((term) => countOccurrences(text, term) > 0);
    if (!present.length)
        return check('title-keywords', 'Keywords in title', FAIL, `None of ${searchTerms.length} search terms found in title.`, `0/${searchTerms.length}`);
    if (present.length < searchTerms.length)
        return check('title-keywords', 'Keywords in title', WARN, `${present.length}/${searchTerms.length} search terms found.`, `${present.length}/${searchTerms.length}`);
    return check('title-keywords', 'Keywords in title', PASS, `All ${searchTerms.length} search terms present.`, `${searchTerms.length}/${searchTerms.length}`);
}

function checkTitleKeywordPosition(text, searchTerms) {
    if (!searchTerms.length)
        return null;
    const first60 = (text || '').slice(0, TITLE_LIMITS.keywordPositionMax).toLowerCase();
    const earlyKeyword = searchTerms.some((term) => first60.includes(term.toLowerCase()));
    if (earlyKeyword)
        return check('title-kw-position', 'Keyword position', PASS, 'Keyword appears in first 60 characters.', 'Front-loaded');
    return check('title-kw-position', 'Keyword position', WARN, 'No keyword in first 60 characters. Front-loading improves ranking.', 'Not front-loaded');
}

function checkTitleCaps(text) {
    const capsCount = hasAllCaps(text);
    if (capsCount > 1)
        return check('title-caps', 'Capitalization', WARN, `${capsCount} ALL-CAPS words. Limit to 1 (e.g. brand acronym).`, `${capsCount} caps`);
    return check('title-caps', 'Capitalization', PASS, 'Capitalization looks good.', 'OK');
}

function checkTitlePowerWords(text) {
    const found = findMatches(text, POWER_WORDS);
    if (found.length)
        return check('title-power', 'Power words', BONUS, `Contains: ${found.join(', ')}`, `${found.length} found`);
    return check('title-power', 'Power words', INFO, 'No power words detected. Consider adding one for CTR.', '0 found');
}

// ── Description checks ──────────────────────────────────────────────

function checkDescriptionLength(text) {
    const len = charCount(text);
    if (len < DESCRIPTION_LIMITS.minChars)
        return check('desc-length', 'Description length', FAIL, `Too short (${len} chars). Aim for ${DESCRIPTION_LIMITS.optimalMinChars}-${DESCRIPTION_LIMITS.optimalMaxChars}.`, `${len} chars`);
    if (len > DESCRIPTION_LIMITS.maxChars)
        return check('desc-length', 'Description length', WARN, `Very long (${len} chars). Consider trimming to ${DESCRIPTION_LIMITS.optimalMaxChars}.`, `${len} chars`);
    if (len > DESCRIPTION_LIMITS.optimalMaxChars)
        return check('desc-length', 'Description length', INFO, `${len} chars. Slightly above optimal (${DESCRIPTION_LIMITS.optimalMaxChars}).`, `${len} chars`);
    if (len < DESCRIPTION_LIMITS.optimalMinChars)
        return check('desc-length', 'Description length', WARN, `${len} chars. Aim for at least ${DESCRIPTION_LIMITS.optimalMinChars}.`, `${len} chars`);
    return check('desc-length', 'Description length', PASS, `${len} chars (optimal range).`, `${len} chars`);
}

function checkDescriptionKeywordDensity(text, searchTerms) {
    if (!searchTerms.length)
        return check('desc-kw-density', 'Keyword density', INFO, 'No search terms defined.', 'N/A');
    const wc = wordCount(text);
    if (!wc) return check('desc-kw-density', 'Keyword density', FAIL, 'Description is empty.', '0 words');

    const issues = [];
    const per500Factor = 500 / Math.max(wc, 1);
    for (const term of searchTerms) {
        const count = countOccurrences(text, term);
        const normalizedCount = Math.round(count * per500Factor);
        if (count === 0) issues.push({term, issue: 'missing'});
        else if (normalizedCount > DESCRIPTION_LIMITS.maxKeywordDensityPer500) issues.push({term, issue: 'overstuffed'});
    }
    if (issues.some((i) => i.issue === 'overstuffed'))
        return check('desc-kw-density', 'Keyword density', WARN, `Keyword stuffing detected: ${issues.filter((i) => i.issue === 'overstuffed').map((i) => `"${i.term}"`).join(', ')}`, 'High');
    if (issues.some((i) => i.issue === 'missing'))
        return check('desc-kw-density', 'Keyword density', WARN, `Missing keywords: ${issues.filter((i) => i.issue === 'missing').map((i) => `"${i.term}"`).join(', ')}`, 'Gaps');
    return check('desc-kw-density', 'Keyword density', PASS, 'Good keyword distribution.', 'Balanced');
}

function checkDescriptionReadability(text) {
    const sents = sentences(text);
    if (sents.length < 2)
        return check('desc-readability', 'Readability', WARN, 'Only 1 sentence. Add more for better readability.', '1 sentence');
    const avgWords = sents.reduce((sum, s) => sum + wordCount(s), 0) / sents.length;
    if (avgWords > DESCRIPTION_LIMITS.maxSentenceWords)
        return check('desc-readability', 'Readability', WARN, `Avg sentence length is ${Math.round(avgWords)} words. Keep under ${DESCRIPTION_LIMITS.maxSentenceWords} for scannability.`, `${Math.round(avgWords)} avg`);
    return check('desc-readability', 'Readability', PASS, `${sents.length} sentences, avg ${Math.round(avgWords)} words each.`, `${Math.round(avgWords)} avg`);
}

function checkDescriptionCTA(text) {
    if (containsAny(text, CTA_WORDS))
        return check('desc-cta', 'Call to action', BONUS, 'Contains action-oriented language.', 'Present');
    return check('desc-cta', 'Call to action', INFO, 'No call-to-action language found. Consider adding one.', 'Missing');
}

function checkDescriptionDuplicate(descText, titleText) {
    if (!descText || !titleText) return null;
    const similarity = jaccardSimilarity(descText, titleText);
    if (similarity > 0.7)
        return check('desc-duplicate', 'Duplicate content', FAIL, `${Math.round(similarity * 100)}% overlap with title. Rewrite to be distinct.`, `${Math.round(similarity * 100)}%`);
    if (similarity > 0.5)
        return check('desc-duplicate', 'Duplicate content', WARN, `${Math.round(similarity * 100)}% overlap with title. Try to differentiate.`, `${Math.round(similarity * 100)}%`);
    return check('desc-duplicate', 'Duplicate content', PASS, 'Content is distinct from title.', 'Unique');
}

// ── Bullet checks ───────────────────────────────────────────────────

function checkBulletLength(text, index) {
    const len = charCount(text);
    if (len < BULLET_LIMITS.minChars)
        return check(`bullet-${index}-length`, 'Bullet length', WARN, `Too short (${len} chars). Aim for ${BULLET_LIMITS.optimalMinChars}-${BULLET_LIMITS.optimalMaxChars}.`, `${len} chars`);
    if (len > BULLET_LIMITS.maxChars)
        return check(`bullet-${index}-length`, 'Bullet length', WARN, `Too long (${len} chars). Keep under ${BULLET_LIMITS.optimalMaxChars}.`, `${len} chars`);
    if (len > BULLET_LIMITS.optimalMaxChars)
        return check(`bullet-${index}-length`, 'Bullet length', INFO, `${len} chars. Slightly above optimal.`, `${len} chars`);
    return check(`bullet-${index}-length`, 'Bullet length', PASS, `${len} chars.`, `${len} chars`);
}

function checkBulletStartWord(text, index) {
    const firstWord = (words(text)[0] || '').toLowerCase();
    if (STOP_WORDS.has(firstWord))
        return check(`bullet-${index}-start`, 'Opening word', WARN, `Starts with "${firstWord}". Lead with a strong keyword or benefit.`, firstWord);
    return check(`bullet-${index}-start`, 'Opening word', PASS, 'Starts with a strong word.', words(text)[0] || '');
}

function checkBulletKeyword(text, searchTerms, index) {
    if (!searchTerms.length) return null;
    const hasKeyword = searchTerms.some((term) => countOccurrences(text, term) > 0);
    if (hasKeyword)
        return check(`bullet-${index}-keyword`, 'Keyword', PASS, 'Contains a search term.', 'Present');
    return check(`bullet-${index}-keyword`, 'Keyword', INFO, 'No search term in this bullet.', 'Missing');
}

// ── Cross-field checks (filler, passive, readability) ───────────────

function checkFillerWords(text, fieldId) {
    const textWords = words(text);
    const found = textWords.filter((w) => FILLER_WORDS.has(w.toLowerCase()));
    if (!found.length)
        return check(`${fieldId}-filler`, 'Filler words', PASS, 'No filler words detected.', 'Clean');
    const unique = Array.from(new Set(found.map((w) => w.toLowerCase())));
    if (found.length > 3)
        return check(`${fieldId}-filler`, 'Filler words', WARN, `${found.length} filler words found: ${unique.join(', ')}. These dilute your copy.`, `${found.length} found`);
    return check(`${fieldId}-filler`, 'Filler words', INFO, `Minor filler: ${unique.join(', ')}. Consider removing.`, `${found.length} found`);
}

function checkPassiveVoice(text, fieldId) {
    const lower = text.toLowerCase();
    const found = PASSIVE_MARKERS.filter((marker) => lower.includes(marker));
    if (!found.length)
        return check(`${fieldId}-passive`, 'Active voice', PASS, 'Uses active voice throughout.', 'Active');
    if (found.length > 2)
        return check(`${fieldId}-passive`, 'Active voice', WARN, `${found.length} passive constructions: "${found.slice(0, 3).join('", "')}". Rewrite for directness.`, `${found.length} passive`);
    return check(`${fieldId}-passive`, 'Active voice', INFO, `Minor passive: "${found.join('", "')}". Consider active voice.`, `${found.length} passive`);
}

function checkFleschReadability(text, fieldId) {
    const wc = wordCount(text);
    if (wc < 15) return null; // too short for meaningful readability
    const score = fleschReadingEase(text);
    const label = fleschToGradeLabel(score);
    if (score >= READABILITY.ecommerceBest)
        return check(`${fieldId}-flesch`, 'Readability', PASS, `Flesch score ${score} — ${label}. Great for product copy.`, `${score}`);
    if (score >= READABILITY.ecommerceMin)
        return check(`${fieldId}-flesch`, 'Readability', INFO, `Flesch score ${score} — ${label}. Acceptable but could be simpler.`, `${score}`);
    return check(`${fieldId}-flesch`, 'Readability', WARN, `Flesch score ${score} — ${label}. Simplify sentences for broader audience.`, `${score}`);
}

// ── Listing-level checks ────────────────────────────────────────────

function checkKeywordCoverage(allText, searchTerms) {
    if (!searchTerms.length)
        return {check: check('listing-kw-coverage', 'Keyword coverage', INFO, 'No search terms defined.', 'N/A'), coverage: []};
    const coverage = searchTerms.map((term) => ({
        term,
        count: countOccurrences(allText, term),
        present: countOccurrences(allText, term) > 0,
    }));
    const presentCount = coverage.filter((c) => c.present).length;
    const pct = Math.round((presentCount / searchTerms.length) * 100);
    let status = PASS;
    if (pct < 50) status = FAIL;
    else if (pct < 80) status = WARN;
    return {
        check: check('listing-kw-coverage', 'Keyword coverage', status, `${presentCount}/${searchTerms.length} terms used (${pct}%).`, `${pct}%`),
        coverage,
    };
}

function checkFieldCompleteness(fields) {
    const hasTitle = fields.some((f) => f.type === 'title' && f.text.trim());
    const hasDesc = fields.some((f) => f.type === 'description' && f.text.trim());
    const bulletCount = fields.filter((f) => f.type === 'bullet' && f.text.trim()).length;
    const issues = [];
    if (!hasTitle) issues.push('title');
    if (!hasDesc) issues.push('description');
    if (bulletCount < BULLET_LIMITS.minCount) issues.push(`${BULLET_LIMITS.minCount}+ bullets`);
    if (issues.length)
        return check('listing-completeness', 'Field completeness', issues.includes('title') ? FAIL : WARN, `Missing: ${issues.join(', ')}.`, `${issues.length} gaps`);
    return check('listing-completeness', 'Field completeness', PASS, 'Title, description, and bullets present.', 'Complete');
}

function checkBulletCount(fields) {
    const count = fields.filter((f) => f.type === 'bullet' && f.text.trim()).length;
    if (count < BULLET_LIMITS.minCount)
        return check('listing-bullet-count', 'Bullet count', WARN, `Only ${count} bullets. Aim for ${BULLET_LIMITS.minCount}-${BULLET_LIMITS.maxCount}.`, `${count}`);
    if (count > BULLET_LIMITS.maxCount)
        return check('listing-bullet-count', 'Bullet count', INFO, `${count} bullets. ${BULLET_LIMITS.minCount}-${BULLET_LIMITS.maxCount} is typical.`, `${count}`);
    return check('listing-bullet-count', 'Bullet count', PASS, `${count} bullets.`, `${count}`);
}

function checkDuplicateStarts(bulletTexts) {
    const starts = bulletTexts.map((t) => words(t).slice(0, 3).join(' ').toLowerCase()).filter(Boolean);
    const seen = new Map();
    starts.forEach((s) => seen.set(s, (seen.get(s) || 0) + 1));
    const dupes = Array.from(seen.entries()).filter(([, count]) => count > 1);
    if (dupes.length)
        return check('listing-dupe-starts', 'Bullet variety', WARN, `${dupes.length} bullet(s) start with the same phrase. Vary openings.`, `${dupes.length} dupes`);
    return check('listing-dupe-starts', 'Bullet variety', PASS, 'All bullets have unique openings.', 'Varied');
}

function checkSpecCompleteness(allText, vars) {
    if (!vars.length) return null;
    const keyVars = vars.filter((v) => KEY_SPEC_VARS.has(v.name) && v.sample);
    if (!keyVars.length) return null;

    const lower = allText.toLowerCase();
    const mentioned = [];
    const missing = [];
    for (const v of keyVars) {
        // Check if either the var name or its value appears in the content
        const valueLower = v.sample.toLowerCase();
        const nameLower = v.name.replace(/_/g, ' ');
        if (lower.includes(valueLower) || lower.includes(nameLower)) {
            mentioned.push(v.name);
        } else {
            missing.push(v);
        }
    }
    const pct = Math.round((mentioned.length / keyVars.length) * 100);
    if (!missing.length)
        return check('listing-specs', 'Spec completeness', PASS, `All ${keyVars.length} key attributes mentioned in content.`, `${pct}%`);
    if (missing.length <= 2)
        return check('listing-specs', 'Spec completeness', WARN,
            `Missing: ${missing.map((v) => `${v.name.replace(/_/g, ' ')} (${v.sample})`).join(', ')}`,
            `${pct}%`);
    return check('listing-specs', 'Spec completeness', FAIL,
        `${missing.length} key specs not in content: ${missing.map((v) => v.name.replace(/_/g, ' ')).join(', ')}`,
        `${pct}%`);
}

// Fuzzy keyword match: checks if the keyword or its singular/plural/stem appears in text
const keywordPresent = (text, keyword) => {
    const lower = text.toLowerCase();
    const kwLower = keyword.toLowerCase().trim();

    // Exact phrase match
    if (lower.includes(kwLower)) return true;

    // Try singular/plural variants
    if (kwLower.endsWith('s') && lower.includes(kwLower.slice(0, -1))) return true;
    if (!kwLower.endsWith('s') && lower.includes(kwLower + 's')) return true;
    if (kwLower.endsWith('es') && lower.includes(kwLower.slice(0, -2))) return true;
    if (kwLower.endsWith('ies') && lower.includes(kwLower.slice(0, -3) + 'y')) return true;

    // All individual words present (for multi-word keywords)
    const kwWords = kwLower.split(/\s+/).filter((w) => w.length > 2);
    if (kwWords.length > 1 && kwWords.every((w) => lower.includes(w) || lower.includes(w.replace(/s$/, '')) || lower.includes(w + 's'))) return true;

    return false;
};

function checkBrandKeywordCoverage(allText, brandProfile) {
    const keywords = [
        ...(brandProfile.primaryKeywords || []),
        ...(brandProfile.secondaryKeywords || []),
    ].filter(Boolean);
    if (!keywords.length) return null;

    const present = keywords.filter((kw) => keywordPresent(allText, kw));
    const count = present.length;

    // A single SKU can't target all brand keywords — 2-4 is healthy
    if (count >= 4)
        return check('listing-brand-kw', 'Brand keywords', PASS, `${count} brand keywords used: ${present.slice(0, 5).join(', ')}${count > 5 ? '...' : ''}`, `${count} used`);
    if (count >= 2)
        return check('listing-brand-kw', 'Brand keywords', PASS, `${count} brand keywords used.`, `${count} used`);
    if (count === 1)
        return check('listing-brand-kw', 'Brand keywords', INFO, `Only 1 brand keyword found. Aim for 2-4 relevant ones.`, `${count} used`);
    return check('listing-brand-kw', 'Brand keywords', INFO, `No brand keywords found. Consider adding 2-4 relevant ones from your brand profile.`, '0 used');
}

function checkWordsToAvoid(allText, brandProfile) {
    const avoided = (brandProfile.wordsAvoid || []).filter(Boolean);
    if (!avoided.length) return null;

    const lower = allText.toLowerCase();
    const found = avoided.filter((w) => lower.includes(w.toLowerCase()));
    if (!found.length)
        return check('listing-words-avoid', 'Words to avoid', PASS, 'No off-brand vocabulary detected.', 'Clean');
    return check('listing-words-avoid', 'Words to avoid', WARN, `Off-brand words found: ${found.join(', ')}. These should be replaced.`, `${found.length} found`);
}

function checkBrandVocabulary(allText, brandProfile) {
    const loved = (brandProfile.wordsLove || []).filter(Boolean);
    if (!loved.length) return null;

    const lower = allText.toLowerCase();
    const used = loved.filter((w) => lower.includes(w.toLowerCase()));

    // Not every preferred word fits every product — using 2-3 is good
    if (used.length >= 3)
        return check('listing-brand-vocab', 'Brand vocabulary', PASS, `Using ${used.length} preferred words: ${used.slice(0, 5).join(', ')}`, `${used.length} used`);
    if (used.length > 0)
        return check('listing-brand-vocab', 'Brand vocabulary', PASS, `Using ${used.length} preferred word(s): ${used.join(', ')}`, `${used.length} used`);
    const suggestions = loved.slice(0, 4).join(', ');
    return check('listing-brand-vocab', 'Brand vocabulary', INFO, `No preferred brand words found. Consider using: ${suggestions}`, '0 used');
}

function checkContentUniqueness(fields) {
    const filledFields = fields.filter((f) => f.text.trim());
    for (let i = 0; i < filledFields.length; i++) {
        for (let j = i + 1; j < filledFields.length; j++) {
            const sim = jaccardSimilarity(filledFields[i].text, filledFields[j].text);
            if (sim > 0.7) {
                return check('listing-uniqueness', 'Content uniqueness', WARN,
                    `"${filledFields[i].label}" and "${filledFields[j].label}" are ${Math.round(sim * 100)}% similar.`,
                    `${Math.round(sim * 100)}%`);
            }
        }
    }
    return check('listing-uniqueness', 'Content uniqueness', PASS, 'Fields are distinct from each other.', 'Unique');
}

// ── Score calculation ───────────────────────────────────────────────

function computeScore(checks) {
    let score = 100;
    for (const c of checks) {
        if (!c) continue;
        if (c.status === FAIL) score -= PENALTY.critical;
        else if (c.status === WARN) score -= PENALTY.warning;
        else if (c.status === BONUS) score += PENALTY.bonus;
    }
    return Math.max(0, Math.min(100, score));
}

export function getGrade(score) {
    for (const g of GRADES) {
        if (score >= g.min) return g;
    }
    return GRADES[GRADES.length - 1];
}

// ── Public API ──────────────────────────────────────────────────────

export function analyzeTitle(text, searchTerms = []) {
    const checks = [
        checkTitleLength(text),
        checkTitleWordCount(text),
        checkTitleKeywords(text, searchTerms),
        checkTitleKeywordPosition(text, searchTerms),
        checkTitleCaps(text),
        checkTitlePowerWords(text),
    ].filter(Boolean);
    const score = computeScore(checks);
    return {score, grade: getGrade(score), checks};
}

export function analyzeDescription(text, searchTerms = [], titleText = '') {
    const checks = [
        checkDescriptionLength(text),
        checkDescriptionKeywordDensity(text, searchTerms),
        checkDescriptionReadability(text),
        checkFleschReadability(text, 'desc'),
        checkFillerWords(text, 'desc'),
        checkPassiveVoice(text, 'desc'),
        checkDescriptionCTA(text),
        checkDescriptionDuplicate(text, titleText),
    ].filter(Boolean);
    const score = computeScore(checks);
    return {score, grade: getGrade(score), checks};
}

export function analyzeBullet(text, searchTerms = [], index = 0) {
    const checks = [
        checkBulletLength(text, index),
        checkBulletStartWord(text, index),
        checkBulletKeyword(text, searchTerms, index),
        checkFillerWords(text, `bullet-${index}`),
        checkPassiveVoice(text, `bullet-${index}`),
    ].filter(Boolean);
    const score = computeScore(checks);
    return {score, grade: getGrade(score), checks};
}

export function analyzeField(field, searchTerms = [], titleText = '', bulletIndex = 0) {
    const text = (field.text || '').trim();
    if (!text) return {score: 0, grade: getGrade(0), checks: [check('empty', 'Content', FAIL, 'Field is empty.', 'Empty')]};

    const fieldTerms = (field.searchTerms || []).length ? field.searchTerms : searchTerms;
    switch (field.type) {
        case 'title': return analyzeTitle(text, fieldTerms);
        case 'description': return analyzeDescription(text, fieldTerms, titleText);
        case 'bullet': return analyzeBullet(text, fieldTerms, bulletIndex);
        default: return {score: 50, grade: getGrade(50), checks: []};
    }
}

export function analyzeListing(listing, vars = [], brandProfile = null) {
    if (!listing?.fields?.length) return null;

    const resolve = (text) => text && text.includes('{{') && vars.length ? resolveVars(text, vars) : (text || '');

    // Create resolved copies of fields for analysis
    const fields = listing.fields.map((f) => ({...f, text: resolve(f.text)}));
    const titleField = fields.find((f) => f.type === 'title');
    const descField = fields.find((f) => f.type === 'description');
    const bulletFields = fields.filter((f) => f.type === 'bullet');
    const titleText = titleField?.text || '';

    // Collect all search terms across fields
    const allSearchTerms = Array.from(new Set(
        fields.flatMap((f) => f.searchTerms || []).filter(Boolean),
    ));
    const allText = fields.map((f) => f.text || '').join(' ');

    // Per-field analysis
    const fieldAnalyses = new Map();
    let bulletIndex = 0;
    for (const field of fields) {
        const idx = field.type === 'bullet' ? bulletIndex++ : 0;
        fieldAnalyses.set(field.id, analyzeField(field, allSearchTerms, titleText, idx));
    }

    // Listing-level checks
    const {check: kwCheck, coverage: keywordCoverage} = checkKeywordCoverage(allText, allSearchTerms);
    const listingChecks = [
        kwCheck,
        checkFieldCompleteness(fields),
        checkBulletCount(fields),
        checkDuplicateStarts(bulletFields.map((f) => f.text || '')),
        checkContentUniqueness(fields),
        checkSpecCompleteness(allText, vars),
        ...(brandProfile ? [
            checkBrandKeywordCoverage(allText, brandProfile),
            checkWordsToAvoid(allText, brandProfile),
            checkBrandVocabulary(allText, brandProfile),
        ] : []),
    ].filter(Boolean);
    const listingScore = computeScore(listingChecks);

    // Weighted overall score
    const titleScore = fieldAnalyses.get(titleField?.id)?.score ?? 0;
    const descScore = fieldAnalyses.get(descField?.id)?.score ?? 0;
    const bulletScores = bulletFields.map((f) => fieldAnalyses.get(f.id)?.score ?? 0);
    const avgBulletScore = bulletScores.length
        ? bulletScores.reduce((a, b) => a + b, 0) / bulletScores.length
        : 0;

    const overallScore = Math.round(
        titleScore * SCORE_WEIGHTS.title +
        descScore * SCORE_WEIGHTS.description +
        avgBulletScore * SCORE_WEIGHTS.bullets +
        listingScore * SCORE_WEIGHTS.listing,
    );

    return {
        score: overallScore,
        grade: getGrade(overallScore),
        fieldAnalyses,
        listingChecks,
        listingScore,
        keywordCoverage,
    };
}
